// ── WHERE A TIMELINE CELL SITS, STATED RATHER THAN IMPLIED ───────────────────────────────────
//
// The Icicle is one CSS-grid column per scene node, and every row (Script · Acts · Sequences ·
// Scenes) shares that template so a cell lines up over its own children. Each cell used to be
// placed with `gridColumn: span N` and NO start, which lets the grid auto-pack each row from
// column 1. That works only while every row's cells cover the whole grid — an assumption nothing
// checked, and one that a real play broke.
//
// ⚠️ MEASURED, movie 36644 ("Three Musketeers", 2026-09-24). Its PROLOGUE was a sequence parented
// to the ROOT, so the Acts row had no cell for it: `act-1` (span 21) packed into columns 1–21 and
// `act-2` into 22–42, with 43–44 empty. Both acts were therefore drawn TWO COLUMNS LEFT of the
// scenes they contain — Act 1's own Scene Three (columns 20–23) ran past its right edge, and the
// act boundary rule landed at 21, inside that scene. The data was correct; only the placement was
// inferred from accumulation instead of read off the node.
//
// So: a cell starts at its first descendant scene's column. A grain with nothing covering some
// stretch then leaves a hole IN THE RIGHT PLACE instead of shifting everything after it — which is
// the difference between a chart that is missing a label and a chart that is lying.
//
// Pure, and in its own file, because this is arithmetic and arithmetic should be testable — the
// component it came out of is 2,300 lines and mounts a browser. See gridPlacement.selftest.mjs.

/**
 * First grid column (1-based) of every node that has scenes under it.
 *
 * @param {Array}  orderedScenes  the scene nodes IN COLUMN ORDER — index i is column i+1. In focus
 *                                mode this is already filtered to the focused act, which is what
 *                                makes the placement follow the zoom without a second code path.
 * @param {Map}    byId           id → node, for walking `parent` upward.
 * @returns {Object} id → 1-based start column. A node with no scenes under it is absent; callers
 *                   fall back to 1, which is what the grid did for it before anyway.
 */
export function startColumns(orderedScenes, byId) {
  const start = {};
  (orderedScenes || []).forEach((s, i) => {
    const col = i + 1;
    let id = s && s.id;
    let guard = 0;
    // The same bounded walk the span/maxScene memo does — a malformed parent chain must not spin.
    while (id && guard++ < 9) {
      if (start[id] === undefined || col < start[id]) start[id] = col;
      const n = byId && byId.get ? byId.get(id) : null;
      id = n ? n.parent : null;
    }
  });
  return start;
}

/**
 * The boundary columns of one grain — where a vertical rule belongs.
 *
 * ⚠️ THE END OF A CELL, NOT A RUNNING TOTAL. `cuts()` used to accumulate spans in sort order, which
 * carries the same error as auto-packing: on 36644 it put the act rule at column 21 instead of 23.
 * A cell's end is its own start plus its own span, and nothing else.
 *
 * @returns {Set} of 0-based boundary indices k, as the overlay's `boundaryLines` uses them.
 */
export function grainBoundaries(cells, start, span) {
  const out = new Set();
  (cells || []).forEach(n => {
    const s = start[n.id];
    if (s === undefined) return;
    out.add(s - 1 + (span[n.id] || 1));
  });
  return out;
}
